import type { EmojiReactionButtonsProps } from './index.types';
import { motion } from 'framer-motion';
import { AVAILABLE_REACTIONS } from 'Modules/ui/emoji/emojiReactions/constants';
import { useReactionHandler } from 'Modules/ui/emoji/emojiReactions/hooks/useReactionHandler';
import React from 'react';
import { cn } from '~/lib/utils';
import { useCurrentUserContext } from '../../hooks/useCurrentContext';
import { EMOJI_BUTTON_ANIMATION } from './index.constants';

/**
 * EmojiReactionButtons - A component that renders a set of emoji reaction buttons
 *
 * This component displays a horizontal row of emoji buttons that users can click
 * to add reactions to messages.
 */
export const EmojiReactionButtons = ({
  reactions,
  messageId,
  closeMenu,
  testID,
}: EmojiReactionButtonsProps): React.ReactElement => {
  const { currentUserId, currentConversationId } = useCurrentUserContext();

  const handleReactionClick = useReactionHandler(
    reactions,
    currentUserId,
    currentConversationId,
    messageId
  );

  const componentTestId = testID
    ? `${testID}-emoji-reaction-button`
    : 'emoji-reaction-button';

  return (
    <div
      className="flex gap-2 justify-center px-2 py-1 group"
      role="toolbar"
      aria-label="Add emoji reaction to message"
      aria-orientation="horizontal"
      data-testid={componentTestId}
    >
      {AVAILABLE_REACTIONS.map(({ emoji, name }) => {
        const hasCurrentUser =
          reactions?.[emoji]?.includes(currentUserId) || false;
        const reactionCount = reactions?.[emoji]?.length || 0;

        return (
          <motion.button
            key={emoji}
            type="button"
            className={cn(
              'flex items-center justify-center size-8 cursor-pointer',
              'group-hover:opacity-40 hover:!opacity-100',
              'border-b-2 focus-visible:ring-2 focus-visible:ring-primary',
              'focus-visible:outline-none transition-[opacity,border-color]',
              hasCurrentUser ? 'border-primary' : 'border-transparent'
            )}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleReactionClick(emoji);
              closeMenu();
            }}
            title={`React with ${name}`}
            aria-label={
              hasCurrentUser
                ? `Remove ${name} reaction (${reactionCount} ${
                    reactionCount === 1 ? 'reaction' : 'reactions'
                  })`
                : `Add ${name} reaction`
            }
            aria-pressed={hasCurrentUser}
            data-testid={`${componentTestId}-${name}`}
            {...EMOJI_BUTTON_ANIMATION}
          >
            <span
              className="flex items-center justify-center size-[18px] text-[18px] leading-none"
              aria-hidden="true"
            >
              {emoji}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};
